/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenHeroCard } from '../../../shared/surface-cards/ScreenHeroCard';
import { spacing } from '../../../shared/theme/spacing';
import { settingsStyles } from '../settingsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors'
import { PaperSheet } from '../../../shared/ticket/PaperSheet';

/** Encabezado de sección compartido (Perfil, Organización, Ajustes de sucursal). */
export function SettingsSectionHeader({ title }: { title: string }) {
  return (
    <View style={settingsStyles.sectionHeader}>
      <Text style={settingsStyles.sectionTitle}>{title}</Text>
    </View>
  );
}

/** Card con filas de navegación — paper Square + padding compacto. */
export function SettingsNavListCard({ children }: { children: React.ReactNode }) {
  return <PaperSheet style={settingsStyles.navListCard}>{children}</PaperSheet>;
}

/** Contenedor de cuerpo con gap estándar entre secciones. */
export function SettingsScreenBody({ children, style }: { children: React.ReactNode; style?: ViewStyle }) {
  return <View style={[localStyles.body, style]}>{children}</View>;
}

type SettingsScreenHeroProps = {
  /** Omitir si el shell ya muestra el título de pantalla. */
  kicker?: string;
  /** Omitir si el shell ya muestra el mismo título. */
  title?: string;
  lead?: string | null;
  style?: ViewStyle;
  onEdit?: () => void;
  editAccessibilityLabel?: string;
  onLeadAction?: () => void;
  leadActionLabel?: string;
  leadActionAccessibilityLabel?: string;
};

/** Hero unificado: kicker opcional + valor principal + detalle secundario. */
export function SettingsScreenHero({
  kicker,
  title,
  lead,
  style,
  onEdit,
  editAccessibilityLabel = 'Editar',
  onLeadAction,
  leadActionLabel = 'Cambiar',
  leadActionAccessibilityLabel = 'Cambiar',
}: SettingsScreenHeroProps) {
  const showTopRow = Boolean(kicker) || Boolean(onEdit);
  return (
    <ScreenHeroCard style={style}>
      {showTopRow ? (
        <View style={localStyles.heroTopRow}>
          {kicker ? <Text style={settingsStyles.formFieldLabel}>{kicker}</Text> : <View />}
          {onEdit ? (
            <TouchableOpacity
              style={settingsStyles.editButton}
              onPress={onEdit}
              accessibilityRole="button"
              accessibilityLabel={editAccessibilityLabel}
            >
              <Ionicons name="create-outline" size={14} color={ticketColors.ink} />
              <Text style={settingsStyles.editButtonText}>Editar</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
      {title ? <Text style={settingsStyles.screenHeroTitle}>{title}</Text> : null}
      {lead ? (
        <View style={localStyles.heroLeadRow}>
          <Text style={[settingsStyles.screenLead, localStyles.heroLeadText]}>{lead}</Text>
          {onLeadAction ? (
            <TouchableOpacity
              style={settingsStyles.editButton}
              onPress={onLeadAction}
              accessibilityRole="button"
              accessibilityLabel={leadActionAccessibilityLabel}
            >
              <Text style={settingsStyles.editButtonText}>{leadActionLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      ) : null}
    </ScreenHeroCard>
  );
}

const localStyles = StyleSheet.create({
  body: {
    gap: spacing.md,
    alignSelf: 'stretch',
  },
  heroTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  heroLeadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  heroLeadText: {
    flex: 1,
    minWidth: 0,
  },
});
